import '../histogram/histogram.scss';
import {memo, useMemo} from 'react';
import {HibuFacet, HibuRangeFilter} from './facetTypes';
import {ReduxNormalizedType} from '../app-types';
import {Histogram, HistogramSnapPoint} from '../histogram/histogram';

type FacetHistogramProps = {
    facetGroupFieldName: string;
    facets: ReduxNormalizedType<HibuFacet>;
    onFacetRangeUpdated: (lowerBound: number, upperBound: number, range: number[]) => void;
    selectedFacetValues: string[];
};

export const FacetHistogramComponent = memo((props: FacetHistogramProps) => {
    const {snapPoints, range} = useMemo(() => {
        const points: number[] = [];
        const sps: HistogramSnapPoint[] = [];

        props.facets.allKeys.forEach((facetKey) => {
            const facet = props.facets.byKey[facetKey] as HibuFacet;
            const rangeFilter = facet.filter as HibuRangeFilter;
            const x0 = new Date(rangeFilter.gte).valueOf();
            const x = new Date(rangeFilter.lt).valueOf();
            points.push(x0);
            points.push(x);
            sps.push(facet);
        });

        const sortedData = points
            .filter((item, pos, self) => self.indexOf(item) === pos) // filter duplicates
            .sort((a, b) => a - b);

        return {
            snapPoints: sps,
            range: [sortedData[0], sortedData[sortedData.length - 1]],
        };
    }, [props.facets.allKeys, props.facets.byKey]);

    const initialState = useMemo(() => {
        const fv = props.selectedFacetValues;
        if (fv?.length === 2) {
            return fv.map((v) => new Date(v).valueOf());
        }
    }, [props.selectedFacetValues]);

    return (
        <Histogram
            snapPoints={snapPoints}
            range={range}
            selection={initialState}
            handleSelection={([lb, ub]) => props.onFacetRangeUpdated(lb, ub, range)}
        />
    );
}, histogramPropsAreEqual);

/**
 * To increase rendering performance: Make a manual check if the histogram component should rerender or not
 */
function histogramPropsAreEqual(prev: FacetHistogramProps, next: FacetHistogramProps): boolean {
    if (
        prev.facetGroupFieldName !== next.facetGroupFieldName ||
        prev.selectedFacetValues?.length !== next.selectedFacetValues?.length ||
        prev.facets.allKeys.length !== next.facets.allKeys.length
    ) {
        return false;
    }
    prev.selectedFacetValues?.forEach((v, i) => {
        if (next.selectedFacetValues[i] !== v) {
            return false;
        }
    });
    prev.facets.allKeys.forEach((v, i) => {
        if (next.facets.allKeys[i] !== v) {
            return false;
        }
    });
    return true;
}
